import { useContext } from "react";
import { useGetPensionCalcData } from "../../../api/pension-robot/useGetPensionCalcData";
import { DEFAULT_PENSION_AGE } from "../../../config/constants";
import { PensionContext } from "../../../contexts/pension-robot/PensionContext";
import { isPensionCalculatedTogether } from "../../../utils/functions";

export const useYearsUntilPensionSelf = () => {
    const { parameters: { personalInfo} } = useContext(PensionContext);
    if( personalInfo.self === undefined ) {
        return 0;
    }
    const selfYearsUntilPension = personalInfo.self.pensionAge - (personalInfo.self.age ?? 0);
    return selfYearsUntilPension;
}

export const useYearsUntilPensionPartner = () => {
    const { parameters: { personalInfo} } = useContext(PensionContext);
    const partnerYearsUntilPension = personalInfo.partner.pensionAge - (personalInfo.partner.age ?? 0);
    return partnerYearsUntilPension;
}

export const useLegalRetirementAge = () => {
    const calcData = useGetPensionCalcData().data;
    const legalRetirementAge = calcData?.legalRetirementAge ?? DEFAULT_PENSION_AGE;
    return legalRetirementAge;
}

export const useIsJointCalculation = () => {
    const { parameters: { personalInfo } } = useContext(PensionContext);
    const isJointCalculation = isPensionCalculatedTogether(personalInfo);
    return isJointCalculation;
}

export const useYearsUntilPensionCalculationStarts = (forcePartnerPerspective: boolean | undefined) => {
    const { parameters: { general } } = useContext(PensionContext);
    const isJointCalculation = useIsJointCalculation();
    const yearsUntilPensionPartner = useYearsUntilPensionPartner();
    const yearsUntilPensionSelf = useYearsUntilPensionSelf();

    // Self
    if (!isJointCalculation)
        return yearsUntilPensionSelf

    // Joint calculation
    if (forcePartnerPerspective !== undefined){
        return forcePartnerPerspective
            ? yearsUntilPensionPartner
            : yearsUntilPensionSelf;
    }
    const yearsUntilPensionInGraphOnResultPage = general.startFromPartnerPensionAge
        ? yearsUntilPensionPartner
        : yearsUntilPensionSelf;

    return yearsUntilPensionInGraphOnResultPage;
}

// Age at displayed Pension date
export const useAgeSelfAtRetirement = (forcePartnerPerspective?: boolean) => {
    const { parameters: { personalInfo } } = useContext(PensionContext);
    const yearsUntilPensionCalculationStarts = useYearsUntilPensionCalculationStarts(forcePartnerPerspective);
    if (!personalInfo.self.age)
        return undefined;

    const ageSelfAtRetirement = personalInfo.self.age + yearsUntilPensionCalculationStarts;
    return ageSelfAtRetirement;
};

// Age at displayed Pension date
export const useAgePartnerAtRetirement = (forcePartnerPerspective?: boolean) => {
    const { parameters: { personalInfo } } = useContext(PensionContext);
    const yearsUntilPensionCalculationStarts = useYearsUntilPensionCalculationStarts(forcePartnerPerspective);
    if (!personalInfo.partner.age)
        return undefined;

    const agePartnerAtRetirement = personalInfo.partner.age + yearsUntilPensionCalculationStarts;
    return agePartnerAtRetirement;
};

export const usePartnerIsRetiredAtPension = (forcePartnerPerspective?: boolean) => {
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);
    const partnerAgeAtPensionStart = useAgePartnerAtRetirement(forcePartnerPerspective);

    if (partnerAgeAtPensionStart === undefined) {
        return undefined;
    }

    const partnerIsRetiredAtPension = partnerAgeAtPensionStart >= personalInfo.partner.pensionAge;
    return partnerIsRetiredAtPension;
}

export const useSelfIsRetiredAtPension = (forcePartnerPerspective?: boolean) => {
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);
    const selfAgeAtPensionStart = useAgeSelfAtRetirement(forcePartnerPerspective);

    if (selfAgeAtPensionStart === undefined) {
        return undefined;
    }

    const selfIsRetiredAtPension = selfAgeAtPensionStart >= personalInfo.self.pensionAge;
    return selfIsRetiredAtPension;
}

// This is based on the retirement age at the displayed graph, unless forced
export const useSelfOlderThanRetirementAge = (forcePartnerPerspective?: boolean) => {
    const legalRetirementAge = useLegalRetirementAge();
    const ageSelfAtRetirement = useAgeSelfAtRetirement(forcePartnerPerspective);
    return ageSelfAtRetirement && ageSelfAtRetirement >= legalRetirementAge;
};

// This is based on the retirement age at the displayed graph, unless forced
export const usePartnerOlderThanRetirementAge = (forcePartnerPerspective?: boolean) => {
    const legalRetirementAge = useLegalRetirementAge();
    const agePartnerAtRetirement = useAgePartnerAtRetirement(forcePartnerPerspective);
    return agePartnerAtRetirement && agePartnerAtRetirement >= legalRetirementAge;
};